/**
 * Phase 1D follow-up — saved IoC filter set dropdown.
 *
 * Dropdown lives above the IoC list filter bar. Three sections:
 *   - "Save current filter as view" (only when something is filtered)
 *   - "My views" — owned by the caller; click to apply, trash to delete
 *   - "Shared" — views other users marked is_shared
 *
 * Applying a view replaces the SPA's filter state with the saved
 * filter blob — the saved filter is the authoritative shape, so we
 * pass the whole object up and let the parent destructure.
 */
import { useEffect, useRef, useState } from "react";
import { Bookmark, BookmarkCheck, ChevronDown, Plus, Share2, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import {
    type IoCSavedView,
    useCreateIoCSavedView,
    useDeleteIoCSavedView,
    useIoCSavedViews,
} from "~/lib/iocs";

interface Props {
    /** The current filter snapshot the SPA would post via "save as view". */
    currentFilter: Record<string, unknown>;
    /** Whether the current filter is non-empty (only then is "save" enabled). */
    hasFilters: boolean;
    /** Apply a saved view's filter to the page state. */
    onApply: (filter: Record<string, unknown>) => void;
}

export function SavedViewsMenu({ currentFilter, hasFilters, onApply }: Props): React.ReactElement {
    const views = useIoCSavedViews();
    const create = useCreateIoCSavedView();
    const remove = useDeleteIoCSavedView();

    const [open, setOpen] = useState(false);
    const [naming, setNaming] = useState(false);
    const [name, setName] = useState("");
    const [shared, setShared] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    // Outside click closes the menu.
    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent): void => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
                setNaming(false);
            }
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    async function doSave(): Promise<void> {
        const trimmed = name.trim();
        if (!trimmed) return;
        try {
            await create.mutateAsync({ name: trimmed, filter: currentFilter, isShared: shared });
            toast.success(`Saved view "${trimmed}"`);
            setName("");
            setShared(false);
            setNaming(false);
            setOpen(false);
        } catch (e) {
            toast.error(e instanceof ApiError ? e.message : "Save failed");
        }
    }

    async function doDelete(v: IoCSavedView): Promise<void> {
        if (!window.confirm(`Delete saved view "${v.name}"?`)) return;
        try {
            await remove.mutateAsync(v.id);
            toast.success("View deleted");
        } catch (e) {
            toast.error(e instanceof ApiError ? e.message : "Delete failed");
        }
    }

    const all = views.data ?? [];
    const mine = all.filter((v) => v.isMine);
    const sharedByOthers = all.filter((v) => !v.isMine && v.isShared);

    return (
        <div ref={ref} style={{ position: "relative" }}>
            <button
                type="button"
                className="btn btn--ghost btn--sm press-crush"
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <Bookmark size={14} aria-hidden />
                <span>Saved views</span>
                {mine.length + sharedByOthers.length > 0 && (
                    <span
                        className="chip"
                        style={{ height: 18, fontSize: "var(--font-size-11)", marginLeft: 4 }}
                    >
                        {mine.length + sharedByOthers.length}
                    </span>
                )}
                <ChevronDown size={12} aria-hidden />
            </button>

            {open && (
                <div
                    role="menu"
                    className="card"
                    style={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        left: 0,
                        zIndex: 50,
                        minWidth: 280,
                        maxWidth: 380,
                        padding: "var(--space-2)",
                        boxShadow: "var(--shadow-floating)",
                    }}
                >
                    {/* Save current filter as view */}
                    {hasFilters && !naming && (
                        <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            style={{ width: "100%", justifyContent: "flex-start" }}
                            onClick={() => setNaming(true)}
                        >
                            <Plus size={14} aria-hidden />
                            <span>Save current filter as view…</span>
                        </button>
                    )}
                    {hasFilters && naming && (
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                void doSave();
                            }}
                            className="stack-xs"
                            style={{ padding: "var(--space-2)" }}
                        >
                            <input
                                autoFocus
                                className="input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="View name"
                                aria-label="View name"
                            />
                            <label
                                className="cluster-xs"
                                style={{ fontSize: "var(--font-size-12)" }}
                            >
                                <input
                                    type="checkbox"
                                    checked={shared}
                                    onChange={(e) => setShared(e.target.checked)}
                                />
                                <Share2 size={12} aria-hidden />
                                <span>Share with workspace</span>
                            </label>
                            <div className="cluster-xs">
                                <button
                                    type="submit"
                                    className="btn btn--primary btn--sm press-crush"
                                    disabled={!name.trim() || create.isPending}
                                >
                                    {create.isPending ? "Saving…" : "Save"}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn--ghost btn--sm"
                                    onClick={() => {
                                        setNaming(false);
                                        setName("");
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}

                    {/* Mine */}
                    {mine.length > 0 && (
                        <>
                            <div
                                className="type-caption"
                                style={{ padding: "var(--space-2)", paddingBottom: 2 }}
                            >
                                Mine
                            </div>
                            {mine.map((v) => (
                                <ViewRow
                                    key={v.id}
                                    view={v}
                                    onApply={() => {
                                        onApply(v.filter);
                                        setOpen(false);
                                    }}
                                    onDelete={() => void doDelete(v)}
                                />
                            ))}
                        </>
                    )}

                    {/* Shared */}
                    {sharedByOthers.length > 0 && (
                        <>
                            <div
                                className="type-caption"
                                style={{
                                    padding: "var(--space-2)",
                                    paddingBottom: 2,
                                    marginTop: "var(--space-1)",
                                }}
                            >
                                <Users size={11} aria-hidden style={{ display: "inline", marginRight: 4 }} />
                                Shared
                            </div>
                            {sharedByOthers.map((v) => (
                                <ViewRow
                                    key={v.id}
                                    view={v}
                                    onApply={() => {
                                        onApply(v.filter);
                                        setOpen(false);
                                    }}
                                />
                            ))}
                        </>
                    )}

                    {!hasFilters && mine.length === 0 && sharedByOthers.length === 0 && (
                        <div
                            className="text-12 text-text-muted"
                            style={{ padding: "var(--space-2)" }}
                        >
                            No saved views yet. Apply some filters and save the set.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

interface ViewRowProps {
    view: IoCSavedView;
    onApply: () => void;
    onDelete?: () => void;
}

function ViewRow({ view, onApply, onDelete }: ViewRowProps): React.ReactElement {
    return (
        <div
            className="cluster-xs"
            style={{
                gap: 0,
                padding: 0,
                justifyContent: "space-between",
                alignItems: "stretch",
            }}
        >
            <button
                type="button"
                role="menuitem"
                className="btn btn--ghost btn--sm"
                onClick={onApply}
                style={{
                    flex: 1,
                    justifyContent: "flex-start",
                    fontWeight: "normal",
                }}
                title={view.ownerName ? `Owner: ${view.ownerName}` : undefined}
            >
                <BookmarkCheck size={12} aria-hidden />
                <span style={{ flex: 1, textAlign: "left" }}>{view.name}</span>
                {view.isShared && (
                    <span className="text-text-subtle" style={{ marginLeft: 4 }}>
                        <Share2 size={11} aria-hidden />
                    </span>
                )}
            </button>
            {onDelete && (
                <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={onDelete}
                    aria-label={`Delete ${view.name}`}
                    title="Delete this view"
                >
                    <Trash2 size={12} aria-hidden />
                </button>
            )}
        </div>
    );
}
